/* global _DEBUG */
// eslint-disable-next-line import/no-extraneous-dependencies
import immutableDevtools from 'immutable-devtools';
import immutable from 'immutable';
import { fromJSON } from 'transit-immutable-js';
import {
  serverRedirect,
  pathlessRoute,
  anonymousThunk,
  transformAction,
  call,
  enter,
  changePageTitle,
  saveScroll,
  restoreScroll,
} from '@respond-framework/rudy';

import { createStoreCommon } from './createStoreCommon';
import { READ_BROWSER_CONTEXT } from './location/actions';
import { initialise as initialiseAuth } from './Auth/initialisers';
import { initialise as initialiseOptimizely } from './Optimizely/initialisers';
import { monitorWindowSize } from './WindowSize/dispatchers';
import * as Utilities from '../web/utilities/legacy-utilities';
import { makeMetricsInit } from '../ReduxStorage/Metrics/webPageDispatchers';
import {
  onPageLeave as userMetricsOnPageLeave,
  onPageArrive as userMetricsonPageArrive,
} from './routeThunks/metrics/userMetrics';
import { onPageArrive as userpilotOnPageArrive } from './routeThunks/userpilot/userpilot';
import { selectPageTitle } from './pageTitle/selectors';
import { scrollToHash } from '../web/rudyMiddlewares/scrollToHash';
import { saveMetric, resetMetrics } from '../web/metrics/user-metrics';
import { loadCurrentUserTosAcceptanceDates } from './routeThunks/common/loadCurrentUserTosAcceptanceDates';
import { startPulse, pausePulse } from '../web/metrics/heartbeat-metrics';
import {
  retrieveTosUpdateDates,
  loadIpAddress,
  loadPythonVersion,
  loadDefaultPortalRegion,
} from './Application/asyncActions';

if (_DEBUG) {
  immutableDevtools(immutable);
}

const readBrowserContext = () => ({
  type: READ_BROWSER_CONTEXT,
  origin: window.location.origin,
  hostname: window.location.hostname,
  protocol: window.location.protocol,
  userAgent: navigator.userAgent,
  csrfToken: Utilities.getCookie('csrftoken'),
});

const ssrState = window.REDUX_STATE && fromJSON(window.REDUX_STATE);

export const createStoreBrowser = (
  previousInjectedReducers,
  initialState = ssrState
) => {
  const rudyOptions = {
    whenLeaving: userMetricsOnPageLeave, // When transitioning to another route
    onComplete: (...args) => {
      userMetricsonPageArrive(...args);
      userpilotOnPageArrive(...args);
    }, // When the route finishes loading
    extra: {
      saveMetric,
      resetMetrics,
      startPulse,
      pausePulse,
    },
  };
  const rudyMiddlewares = [
    /**
     * These middlewares are "short circuiting" - i.e. for actions where
     * they are applicable, they don't call `next()` and therefore skip
     * subsequent middlewares
     */
    serverRedirect,
    anonymousThunk,
    pathlessRoute('thunk'),

    /**
     * scrollToHash is before transformAction so that it is possible to
     * scroll to a hash on the current route
     */
    scrollToHash,

    /**
     * transformAction is responsible for adding all the extra properties
     * available on routing actions in `action.location`. It also filters
     * out any keys on the action that are not serialiseable to the URL,
     * and filters out dispatches to exactly the current route.
     */
    transformAction,

    /**
     * This sets the current scroll position (before changing the page content)
     * as the one to restore if this history entry is revisited e.g. via the
     * back buttons.
     */
    saveScroll,

    call('beforeLeave', { prev: true }),
    call('beforeEnter', { runOnServer: true }),
    call('whenLeaving', { prev: true }),
    enter, // change URL, dispatch action to reducers
    changePageTitle({ title: selectPageTitle }),
    call('onLeave', { prev: true }),
    call('onEnter', { runOnHydrate: true }),
    call('beforeThunk', { runOnServer: true }),
    call('thunk', { runOnServer: true }),
    restoreScroll,
    call('onComplete', { runOnServer: true }),
  ];
  const { store, firstRoute, rudyApi, ...rest } = createStoreCommon(
    previousInjectedReducers,
    initialState,
    window.location.origin,
    rudyOptions,
    rudyMiddlewares
  );
  const {
    options: {
      extra: { metrics },
    },
  } = rudyApi;

  store.dispatch(readBrowserContext());
  store.dispatch(monitorWindowSize());

  /* TODO: move this to happen in onComplete/onPageArrive
   * (having made logEvent/identify async) */
  store.dispatch(
    makeMetricsInit({
      metrics,
      saveMetric,
      resetMetrics,
      startPulse,
      pausePulse,
    })
  );

  // Temporary exception to the rule of not initialising the store from the DOM
  // For backwards compatibility
  initialiseAuth(store.dispatch, store.getState);

  store.dispatch(retrieveTosUpdateDates());
  store.dispatch(loadIpAddress());
  store.dispatch(loadPythonVersion());
  store.dispatch(loadDefaultPortalRegion());

  // The Optimizely data should be available at startup, this is the simplest
  // way to do it for now
  initialiseOptimizely(store.dispatch, store.getState);

  loadCurrentUserTosAcceptanceDates(store.dispatch, store.getState);

  store.dispatch(firstRoute());
  return {
    store,
    rudyApi,
    ...rest,
  };
};
